import React, { useState, useCallback, useRef } from 'react'
import insertStyles from '../../modules/projects/components/DescriptionEditor/DescriptionEditor.module.css'
import QuillInsertDropdown from './QuillInsertDropdown'

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i
const isImageFile = (file) => {
    if (!file) return false
    if (file.type?.startsWith('image/')) return true
    // Some OS/browser picks leave MIME empty — fall back to extension.
    return IMAGE_EXTENSIONS.test(file.name || '')
}

const INSERT_SECTIONS = [
    {
        items: [
            {
                key: 'bullet',
                label: 'Bulleted list',
                icon: 'ri:list-unordered',
            },
            { key: 'ordered', label: 'Numbered list', icon: 'ri:list-ordered' },
        ],
    },
    {
        items: [{ key: 'quote', label: 'Quote', icon: 'ri:double-quotes-l' }],
    },
    {
        items: [
            { key: 'emoji', label: 'Emoji', icon: 'ri:emoji-sticker-line' },
            { key: 'image', label: 'Image', icon: 'ri:image-line' },
            { key: 'mention', label: 'Mention', icon: 'ri:at-line' },
            { key: 'link', label: 'Link', icon: 'ri:link' },
        ],
    },
]

const MentionEditorInsertMenu = ({ quillRef }) => {
    const imageInputRef = useRef(null)
    const savedEmojiRange = useRef(null)

    const [emojiOpen, setEmojiOpen] = useState(false)
    const [linkPopup, setLinkPopup] = useState({
        open: false,
        text: '',
        url: '',
        savedRange: null,
    })

    const closeLinkPopup = useCallback(() => {
        setLinkPopup({ open: false, text: '', url: '', savedRange: null })
        quillRef.current?.focus()
    }, [quillRef])

    const closeEmojiPicker = useCallback(() => {
        setEmojiOpen(false)
        quillRef.current?.focus()
    }, [quillRef])

    const handleLinkSubmit = useCallback(() => {
        const q = quillRef.current
        if (!q) return
        const { text, url, savedRange } = linkPopup
        if (!url.trim()) {
            if (savedRange?.length) q.format('link', false)
            closeLinkPopup()
            return
        }
        const href = url.trim()
        if (savedRange?.length) {
            q.setSelection(savedRange.index, savedRange.length, 'silent')
            q.format('link', href)
        } else {
            const insertText = text.trim() || href
            const idx = savedRange?.index ?? q.getLength()
            q.insertText(idx, insertText, 'link', href, 'user')
            q.setSelection(idx + insertText.length, 0)
        }
        closeLinkPopup()
    }, [linkPopup, closeLinkPopup, quillRef])

    const handleEmojiSelect = useCallback(
        (emoji) => {
            const q = quillRef.current
            if (!q) return
            q.focus()
            const range = savedEmojiRange.current ??
                q.getSelection(true) ?? { index: q.getLength(), length: 0 }
            const idx = range.index + range.length
            q.insertText(idx, emoji.native, 'user')
            q.setSelection(idx + emoji.native.length, 0)
            closeEmojiPicker()
        },
        [quillRef, closeEmojiPicker]
    )

    const handleInsertItem = useCallback(
        (key) => {
            const q = quillRef.current
            if (!q) return
            q.focus()
            const range = q.getSelection(true) ?? {
                index: q.getLength(),
                length: 0,
            }

            switch (key) {
                case 'bullet':
                    q.format('list', 'bullet')
                    break
                case 'ordered':
                    q.format('list', 'ordered')
                    break
                case 'quote': {
                    const fmt = q.getFormat(range)
                    q.format('blockquote', !fmt.blockquote)
                    break
                }
                case 'image':
                    imageInputRef.current?.click()
                    return false
                case 'mention': {
                    const idx = range.index + range.length
                    q.insertText(idx, '@', 'user')
                    q.setSelection(idx + 1, 0, 'user')
                    break
                }
                case 'emoji':
                    savedEmojiRange.current = q.getSelection(true)
                    setEmojiOpen(true)
                    // Close insert menu; emoji popover stays open
                    break
                default:
                    break
            }
        },
        [quillRef]
    )

    const handleImageFile = useCallback(
        (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            if (!isImageFile(file)) {
                return
            }
            const reader = new FileReader()
            reader.onload = (ev) => {
                const q = quillRef.current
                if (!q) return
                const range = q.getSelection(true) ?? {
                    index: q.getLength(),
                    length: 0,
                }
                q.insertEmbed(range.index, 'image', ev.target.result)
                q.setSelection(range.index + 1)
            }
            reader.readAsDataURL(file)
        },
        [quillRef]
    )

    return (
        <QuillInsertDropdown
            sections={INSERT_SECTIONS}
            onInsertItem={handleInsertItem}
            plusButtonClassName={`${insertStyles.toolbarBtn} ${insertStyles.plusBtn}`}
            wrapClassName={`mention-insert-group ${insertStyles.insertGroup}`}
            buttonTitle="Insert"
            showLinkPopover
            emoji={{
                open: emojiOpen,
                onOpenChange: (open) => {
                    if (open) setEmojiOpen(true)
                    else closeEmojiPicker()
                },
                onSelect: handleEmojiSelect,
                savedRangeRef: savedEmojiRange,
            }}
            link={{
                state: linkPopup,
                onStateChange: setLinkPopup,
                onSubmit: handleLinkSubmit,
                onClose: closeLinkPopup,
                quillRef,
            }}
            image={{
                inputRef: imageInputRef,
                onChange: handleImageFile,
            }}
        />
    )
}

export default MentionEditorInsertMenu
